import React, { useState, useEffect } from 'react';
import {
    TextField,
    Popover,
    Box,
    Typography,
    IconButton,
    Button,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

interface SimpleDatePickerProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
}

const SimpleDatePicker: React.FC<SimpleDatePickerProps> = ({
    value = '',
    onChange,
    placeholder = 'MM/DD/YYYY'
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        value ? new Date(value) : null
    );
    const [inputValue, setInputValue] = useState(value || '');
    const [showYearMonthSelector, setShowYearMonthSelector] = useState(false);

    // Update input value when prop value changes
    useEffect(() => {
        setInputValue(value || '');
        if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                setSelectedDate(date);
                setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
            }
        }
    }, [value]);

    const handleCalendarIconClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget.parentElement as HTMLElement);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setShowYearMonthSelector(false);
    };

    const formatDate = (date: Date): string => {
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    const parseDate = (dateString: string): Date | null => {
        // Support multiple date formats
        const formats = [
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY or M/D/YYYY
            /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // MM-DD-YYYY or M-D-YYYY
            /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
        ];

        for (const format of formats) {
            const match = dateString.match(format);
            if (match) {
                let month, day, year;
                if (format.source.startsWith('^(\\d{4})')) {
                    // YYYY-MM-DD format
                    [, year, month, day] = match;
                } else {
                    // MM/DD/YYYY or MM-DD-YYYY format
                    [, month, day, year] = match;
                }

                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                if (!isNaN(date.getTime()) &&
                    date.getFullYear() === parseInt(year) &&
                    date.getMonth() === parseInt(month) - 1 &&
                    date.getDate() === parseInt(day)) {
                    return date;
                }
            }
        }
        return null;
    };

    const formatDateInput = (value: string): string => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        if (digits.length > 4) {
            return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        }
        if (digits.length > 2) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return digits;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = formatDateInput(event.target.value);
        setInputValue(newValue);

        // Try to parse the date as the user types
        const parsedDate = parseDate(newValue);
        if (parsedDate) {
            setSelectedDate(parsedDate);
            setCurrentMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
            if (onChange) {
                onChange(formatDate(parsedDate));
            }
        } else if (newValue === '') {
            setSelectedDate(null);
            if (onChange) {
                onChange('');
            }
        }
    };

    const handleInputBlur = () => {
        // If the input has a value but no valid date was parsed, try one more time
        if (inputValue && !selectedDate) {
            const parsedDate = parseDate(inputValue);
            if (parsedDate) {
                const formattedDate = formatDate(parsedDate);
                setInputValue(formattedDate);
                setSelectedDate(parsedDate);
                setCurrentMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
                if (onChange) {
                    onChange(formattedDate);
                }
            }
        } else if (selectedDate) {
            // Ensure the input shows the properly formatted date
            const formattedDate = formatDate(selectedDate);
            setInputValue(formattedDate);
        }
    };

    const getMonthName = (date: Date): string => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const getDaysInMonth = (date: Date): Date[] => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const days: Date[] = [];
        const currentDate = new Date(startDate);

        for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

    const isSameDay = (date1: Date, date2: Date): boolean => {
        return date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear();
    };

    const handleDayClick = (date: Date) => {
        setSelectedDate(date);
        const formattedDate = formatDate(date);
        setInputValue(formattedDate);
        if (onChange) {
            onChange(formattedDate);
        }
        handleClose();
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth(prev => {
            const newMonth = new Date(prev);
            if (direction === 'prev') {
                newMonth.setMonth(prev.getMonth() - 1);
            } else {
                newMonth.setMonth(prev.getMonth() + 1);
            }
            return newMonth;
        });
    };

    const handleYearChange = (year: number) => {
        setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    };

    const handleMonthChange = (month: number) => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), month, 1));
    };

    const renderYearMonthSelector = () => {
        const currentYear = currentMonth.getFullYear();
        const currentMonthIndex = currentMonth.getMonth();

        // Generate year options (current year ± 100 years)
        const yearOptions = [];
        for (let year = currentYear - 100; year <= currentYear + 10; year++) {
            yearOptions.push(year);
        }

        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        return (
            <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                        value={currentMonthIndex}
                        onChange={(e) => handleMonthChange(e.target.value as number)}
                        variant="outlined"
                    >
                        {monthNames.map((month, index) => (
                            <MenuItem key={index} value={index}>
                                {month}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                        value={currentYear}
                        onChange={(e) => handleYearChange(e.target.value as number)}
                        variant="outlined"
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {yearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    size="small"
                    onClick={() => setShowYearMonthSelector(false)}
                    variant="outlined"
                    sx={{ minWidth: 60 }}
                >
                    Done
                </Button>
            </Box>
        );
    };

    const renderCalendar = () => {
        const days = getDaysInMonth(currentMonth);
        const today = new Date();

        return (
            <Box sx={{ width: showYearMonthSelector ? '360px' : '280px' }}>
                {showYearMonthSelector ? (
                    renderYearMonthSelector()
                ) : (
                    <>
                        {/* Header */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            pb: 1
                        }}>
                            <IconButton onClick={() => navigateMonth('prev')} size="small">
                                <ChevronLeftIcon />
                            </IconButton>
                            <Button
                                onClick={() => setShowYearMonthSelector(true)}
                                sx={{
                                    fontWeight: 600,
                                    color: '#0E1944',
                                    '&:hover': {
                                        backgroundColor: '#EEF5FF',
                                    }
                                }}
                            >
                                {getMonthName(currentMonth)}
                            </Button>
                            <IconButton onClick={() => navigateMonth('next')} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>

                        {/* Calendar Grid */}
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                gap: '4px',
                            }}>
                                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                                    <Typography key={day} variant="caption" sx={{
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: '#676E8B',
                                        textAlign: 'center',
                                        p: 0.5
                                    }}>
                                        {day}
                                    </Typography>
                                ))}
                                {days.map((day, index) => {
                                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                                    const isToday = isSameDay(day, today);
                                    const isSelected = selectedDate && isSameDay(day, selectedDate);

                                    return (
                                        <Button
                                            key={index}
                                            onClick={() => handleDayClick(day)}
                                            disabled={!isCurrentMonth}
                                            sx={{
                                                minWidth: '32px',
                                                height: '32px',
                                                padding: 0,
                                                borderRadius: '50%',
                                                fontSize: '13px',
                                                fontWeight: isToday ? 600 : 400,
                                                color: isSelected ? 'white' : isToday ? '#4566EB' : '#0E1944',
                                                backgroundColor: isSelected ? '#4566EB' : 'transparent',
                                                border: isToday && !isSelected ? '1px solid #4566EB' : 'none',
                                                opacity: isCurrentMonth ? 1 : 0.3,
                                                '&:hover': {
                                                    backgroundColor: isSelected ? '#4566EB' : '#EEF5FF',
                                                },
                                                '&:disabled': {
                                                    opacity: 0.3,
                                                }
                                            }}
                                        >
                                            {day.getDate()}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <TextField
                placeholder={placeholder}
                variant="outlined"
                size="small"
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                InputProps={{
                    endAdornment: (
                        <IconButton
                            onClick={handleCalendarIconClick}
                            size="small"
                            sx={{ p: 0.5 }}
                        >
                            <CalendarIcon sx={{ color: '#676E8B', fontSize: 18, cursor: 'pointer' }} />
                        </IconButton>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        height: '36px',
                    }
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0px 8px 32px rgba(14, 25, 68, 0.15)',
                        overflow: 'hidden',
                    }
                }}
            >
                {renderCalendar()}
            </Popover>
        </>
    );
};

export default SimpleDatePicker; 